import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  SegmentItem,
  type SegmentItemProps,
} from "@/toggle-ui-kit/segment-control";
import { isBetaFeatureEnabled } from "./feature-flag";
import { BetaFeatureOnly } from "./marks";

export interface BetaFeatureSegmentControlItem<T extends string = string> {
  value: T;
  label: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  disabled?: boolean;
  isBetaFeature?: boolean;
  className?: string;
}

interface BetaFeatureSegmentControlProps<T extends string = string> {
  items: BetaFeatureSegmentControlItem<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: SegmentItemProps["size"];
  className?: string;
}

// Keep this markup in sync with toggle-ui-kit/SegmentControl until SegmentControl
// exposes an item decoration API for beta-only options.
export const BetaFeatureSegmentControl = <T extends string = string>({
  items,
  value,
  onChange,
  size = "base",
  className,
}: BetaFeatureSegmentControlProps<T>) => {
  const betaFeatureEnabled = isBetaFeatureEnabled();
  const visibleItems = items.filter(
    (item) => betaFeatureEnabled || !item.isBetaFeature,
  );

  const renderItem = (item: BetaFeatureSegmentControlItem<T>) => (
    <SegmentItem
      key={item.value}
      selected={value === item.value}
      size={size}
      disabled={item.disabled}
      leftIcon={item.leftIcon}
      rightIcon={item.rightIcon}
      className={item.className}
      onClick={() => {
        if (!item.disabled) {
          onChange(item.value);
        }
      }}
    >
      {item.label}
    </SegmentItem>
  );

  return (
    <div
      role="radiogroup"
      className={cn("inline-flex items-center gap-3 flex-nowrap", className)}
    >
      {visibleItems.map((item) => {
        if (item.isBetaFeature) {
          // Filtering hides disabled beta items; wrapping marks visible beta items for inspector mode.
          return (
            <BetaFeatureOnly key={item.value}>
              {renderItem(item)}
            </BetaFeatureOnly>
          );
        }

        return renderItem(item);
      })}
    </div>
  );
};
